import { TransferProgressEvent } from "@azure/core-http";
import { BlobClient, BlobLeaseClient, BlobRequestConditions, BlockBlobClient, BlockBlobParallelUploadOptions } from "@azure/storage-blob";

export interface IBlobLease {
    leaseId: string;
    renew(): Promise<void>;
    release(): Promise<void>;
}

export interface ISasUriProvider {
    getSasUri(containerName: "snapshots" | "generated" | "temp", blobName: string): Promise<string>;
}

export interface IBlob {
    blobName: string;
    etag?: string;
    blob: Blob;
    ignoreIfNoneMatch?: boolean;
}

export interface IBlobUploadResult {
    versionId: string;
    etag: string;
}

class BlobLease implements IBlobLease {
    private readonly blobLeaseClient: BlobLeaseClient;

    readonly leaseId: string;

    constructor(blobLeaseClient: BlobLeaseClient) {
        this.leaseId = blobLeaseClient.leaseId;
        this.blobLeaseClient = blobLeaseClient;
    }

    async renew(): Promise<void> {
        await this.blobLeaseClient.renewLease();
    }

    async release(): Promise<void> {
        await this.blobLeaseClient.releaseLease();
    }
}

export class BlobDataSource {
    private readonly sasUriProvider: ISasUriProvider;

    constructor(sasUriProvider: ISasUriProvider) {
        this.sasUriProvider = sasUriProvider;
    }

    async acquireBlobLease(
        blobName: string,
        duration: number,
        containerName?: string & ("snapshots" | "generated"),
    ): Promise<IBlobLease> {
        let sasUri = await this.sasUriProvider.getSasUri(containerName ?? "snapshots", blobName);
        let client = new BlobClient(sasUri);
        let blc = client.getBlobLeaseClient();
        await blc.acquireLease(duration);
        return new BlobLease(blc);
    }

    async downloadBlobLink(
        blobName: string,
        versionId?: string,
        containerName?: string & ("snapshots" | "generated"),
    ): Promise<string> {
        let sasUri = await this.sasUriProvider.getSasUri(containerName ?? "snapshots", blobName);
        return sasUri;
    }

    async downloadBlob(
        blobName: string,
        versionId?: string | null,
        containerName?: string & ("snapshots" | "generated"),
    ): Promise<IBlob> {
        let sasUri = await this.sasUriProvider.getSasUri(containerName ?? "snapshots", blobName);
        let client = new BlockBlobClient(sasUri);

        if (versionId) {

            client = client.withVersion(versionId) as BlockBlobClient;
            console.debug("[apii] downloadBlob.withVersion", versionId);
        }

        let response = await client.download();
        let blob = await response.blobBody;

        return {
            blobName: blobName,
            etag: response.etag!,
            blob: blob!
        };
    }

    async uploadBlob(
        blob: IBlob,
        metadata?: { [propertyName: string]: string; },
        leaseId?: string,
        onProgress?: (progress: TransferProgressEvent) => void,
        containerName?: string & ("snapshots" | "generated" | "temp"),
    ): Promise<IBlobUploadResult> {
        let sasUri = await this.sasUriProvider.getSasUri(containerName ?? "snapshots", blob.blobName);
        let client = new BlockBlobClient(sasUri);

        let conditions: BlobRequestConditions = {};
        if (leaseId)
            conditions.leaseId = leaseId;

        if (blob.etag) {

            // if V has been concatenated to version number
            // example: V2023-06-07T02:42:17.1830679Z
            const ifMatch = /V\d\d\d\d/.test(blob.etag)
                ? blob.etag.substring(1)
                : blob.etag;

            conditions.ifMatch = ifMatch;
        } else if (!blob.ignoreIfNoneMatch) {
            // see https://stackoverflow.com/questions/32981691/is-the-azure-put-blob-operation-atomic
            conditions.ifNoneMatch = '*'
        }

        let options: BlockBlobParallelUploadOptions = {
            blockSize: 1024 * 1000, // 1mb
            concurrency: 3,
            conditions: conditions
        };

        if (metadata) {
            options.metadata = metadata;
        }

        if (onProgress)
            options.onProgress = onProgress;


        // https://docs.microsoft.com/en-us/javascript/api/@azure/storage-blob/blockblobclient?view=azure-node-latest#@azure-storage-blob-blockblobclient-uploadbrowserdata
        // use uploadData instead of uploadBrowserData
        let response = await client.uploadData(blob.blob, options);

        // return the new version
        return {
            etag: response.etag!,
            versionId: response.versionId!
        };
    }

}